<template>
    <div class="admin-dashboard">
        <div class="row g-2 g-md-3 mb-3">
            <div v-for="card in statCards" :key="card.key" class="col-6 col-lg-3">
                <router-link :to="card.to" class="admin-stat admin-stat--link" :class="card.mod">
                    <i class="bi admin-stat__ico" :class="card.icon" aria-hidden="true"></i>
                    <div>
                        <span class="admin-stat__num">{{ card.value }}</span>
                        <span class="admin-stat__label">{{ card.label }}</span>
                    </div>
                </router-link>
            </div>
        </div>

        <div v-if="alertas.length" class="admin-alerts-bar mb-3">
            <router-link v-for="a in alertas" :key="a.key" :to="a.to" class="admin-alerts-bar__item">
                <span class="badge rounded-pill" :class="a.tone">{{ a.count }}</span>
                {{ a.text }}
            </router-link>
        </div>

        <section class="admin-shortcuts mb-4" aria-label="Accesos rápidos">
            <h2 class="admin-shortcuts__title">Accesos rápidos</h2>
            <nav class="admin-shortcuts__grid">
                <router-link
                    v-for="s in shortcuts"
                    :key="s.to"
                    :to="s.to"
                    class="admin-shortcut"
                    :class="{ 'admin-shortcut--accent': s.accent }"
                >
                    <i class="bi admin-shortcut__ico" :class="s.icon" aria-hidden="true"></i>
                    <span>{{ s.label }}</span>
                </router-link>
            </nav>
        </section>

        <section class="admin-card-block">
            <div class="admin-card-block__head d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h2 class="admin-card-block__title mb-0">Actividad reciente</h2>
                <router-link to="/u/logs" class="small text-decoration-none">Ver todo →</router-link>
            </div>
            <div v-if="cargandoLogs" class="text-center text-muted py-4">
                <div class="spinner-border spinner-border-sm" role="status"></div>
            </div>
            <div v-else-if="!logsRecientes.length" class="text-muted small py-3">Sin movimientos recientes.</div>
            <div v-else class="table-responsive">
                <table class="table table-sm align-middle mb-0 admin-recent-table">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Negocio</th>
                            <th>Acción</th>
                            <th>Detalle</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="l in logsRecientes" :key="l.log_id">
                            <td class="text-nowrap small text-muted">{{ fmtFecha(l.creado_en) }}</td>
                            <td class="small">@{{ l.usuario_negocio }}</td>
                            <td class="small"><span class="badge rounded-pill text-bg-primary-subtle text-primary-emphasis">{{ l.accion }}</span></td>
                            <td class="small text-muted text-truncate" style="max-width: 280px">{{ resumenLog(l) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'AdminDashboard',
    data() {
        return {
            stats: { total: 0, activos: 0, vencidos: 0, porVencer: 0, pendientes: 0, pedidosNuevos: 0 },
            logsRecientes: [],
            cargandoLogs: true,
            shortcuts: [
                { to: '/u/negocios', label: 'Negocios', icon: 'bi-building' },
                { to: '/u/negocios?estado=pendientes', label: 'Solicitudes', icon: 'bi-person-check' },
                { to: '/u/admin/pagos', label: 'Pagos', icon: 'bi-cash-stack' },
                { to: '/u/admin/negocios/nuevo', label: 'Nuevo negocio', icon: 'bi-person-plus', accent: true },
                { to: '/u/logs', label: 'Actividad', icon: 'bi-clock-history' },
            ],
        };
    },
    computed: {
        statCards() {
            return [
                { key: 'total', label: 'Negocios', value: this.stats.total, icon: 'bi-building', mod: '', to: '/u/negocios' },
                { key: 'pendientes', label: 'Pendientes', value: this.stats.pendientes, icon: 'bi-hourglass-split', mod: 'admin-stat--soon', to: '/u/negocios?estado=pendientes' },
                { key: 'activos', label: 'Activos', value: this.stats.activos, icon: 'bi-check2-circle', mod: 'admin-stat--ok', to: '/u/negocios?estado=activos' },
                { key: 'vencidos', label: 'Vencidos', value: this.stats.vencidos, icon: 'bi-exclamation-triangle', mod: 'admin-stat--warn', to: '/u/negocios?estado=vencidos' },
            ];
        },
        alertas() {
            const items = [];
            if (this.stats.pendientes > 0) {
                items.push({ key: 'pendientes', count: this.stats.pendientes, text: 'solicitud(es) de registro', tone: 'text-bg-warning', to: '/u/negocios?estado=pendientes' });
            }
            if (this.stats.vencidos > 0) {
                items.push({ key: 'vencidos', count: this.stats.vencidos, text: 'con plan vencido', tone: 'text-bg-danger', to: '/u/negocios?estado=vencidos' });
            }
            if (this.stats.porVencer > 0) {
                items.push({ key: 'porVencer', count: this.stats.porVencer, text: 'vencen pronto', tone: 'text-bg-warning', to: '/u/negocios?estado=por_vencer' });
            }
            return items;
        },
    },
    mounted() {
        this.cargar();
    },
    methods: {
        async cargar() {
            await Promise.all([this.cargarStats(), this.cargarLogs()]);
        },
        async cargarStats() {
            try {
                const { data } = await axios.get('/listarNegocios');
                const lista = Array.isArray(data) ? data : [];
                const ahora = new Date();
                const enUnaSemana = new Date(ahora.getTime() + 7 * 24 * 60 * 60 * 1000);
                let vencidos = 0;
                let porVencer = 0;
                let pendientes = 0;
                for (const n of lista) {
                    if (n.cuentaAprobacion === 'pendiente') pendientes += 1;
                    if (!n.fechaVence) continue;
                    const f = new Date(n.fechaVence);
                    if (Number.isNaN(f.getTime())) continue;
                    if (f < ahora) vencidos += 1;
                    else if (f <= enUnaSemana) porVencer += 1;
                }
                this.stats = {
                    total: lista.length,
                    activos: lista.length - vencidos - pendientes,
                    vencidos,
                    porVencer,
                    pendientes,
                    pedidosNuevos: 0,
                };
            } catch (e) {
                console.error(e);
            }
        },
        async cargarLogs() {
            this.cargandoLogs = true;
            try {
                const { data } = await axios.get('/logs', { params: { limit: 8 } });
                this.logsRecientes = Array.isArray(data) ? data : [];
            } catch (e) {
                console.error(e);
            } finally {
                this.cargandoLogs = false;
            }
        },
        fmtFecha(v) {
            try {
                return new Date(v).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
            } catch {
                return v;
            }
        },
        resumenLog(l) {
            const partes = [];
            if (l.entidad) partes.push(l.entidad + (l.entidad_id ? ` #${l.entidad_id}` : ''));
            if (l.detalle) {
                try {
                    const d = JSON.parse(l.detalle);
                    partes.push(Object.entries(d).map(([k, v]) => `${k}: ${v}`).join(', '));
                } catch {
                    partes.push(l.detalle);
                }
            }
            return partes.join(' · ') || '—';
        },
    },
};
</script>

<style scoped>
.admin-dashboard {
    max-width: 1100px;
    margin: 0 auto;
}

.admin-stat {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 0.85rem 1rem;
    height: 100%;
    text-decoration: none;
    color: inherit;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.admin-stat--link:hover {
    border-color: #c7d2fe;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
    color: inherit;
}

.admin-stat__ico {
    font-size: 1.45rem;
    color: #6366f1;
    flex-shrink: 0;
}

.admin-stat--ok .admin-stat__ico { color: #16a34a; }
.admin-stat--warn .admin-stat__ico { color: #dc2626; }
.admin-stat--soon .admin-stat__ico { color: #d97706; }

.admin-stat__num {
    display: block;
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1;
}

.admin-stat__label {
    display: block;
    font-size: 0.74rem;
    color: #64748b;
    margin-top: 0.12rem;
}

.admin-alerts-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.admin-alerts-bar__item {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.45rem 0.75rem;
    border-radius: 999px;
    background: #fff;
    border: 1px solid #e2e8f0;
    font-size: 0.82rem;
    color: #334155;
    text-decoration: none;
}

.admin-alerts-bar__item:hover {
    background: #f8fafc;
    color: #0f172a;
}

.admin-shortcuts__title {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    margin: 0 0 0.65rem;
}

.admin-shortcuts__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
}

@media (min-width: 576px) {
    .admin-shortcuts__grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}

.admin-shortcut {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    min-height: 96px;
    padding: 0.85rem 0.5rem;
    text-decoration: none;
    color: #0f172a;
    background: #fff;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
    font-size: 0.8125rem;
    font-weight: 600;
    text-align: center;
    transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.admin-shortcut:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
    border-color: #c7d2fe;
    color: #0f172a;
}

.admin-shortcut--accent {
    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
    border-color: #c7d2fe;
}

.admin-shortcut__ico {
    font-size: 1.5rem;
    color: #6366f1;
}

.admin-card-block {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 1rem 1.1rem;
}

.admin-card-block__head {
    margin-bottom: 0.75rem;
}

.admin-card-block__title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
}

.admin-recent-table th {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    border-bottom-width: 1px;
}
</style>
